import { FC } from "react";
import { observer } from "mobx-react";
import { OFTableController } from "../OFTableController";
import { RenderCellContent } from "./RenderCellContent";
import { useDrag, useDrop, DragPreviewImage } from "react-dnd";
import { dndMultipleImage, dndSingleImage } from "./dndImages";

const DraggableRow: FC<any> = observer(({ ndnConfig, cols, rowData, multiple }) => {
  const [collected, drag, dragPreview] = useDrag(() => ({
    type: "item",
    item: { item: rowData },
    canDrag: ndnConfig.canDrag ? ndnConfig.canDrag(rowData) : true,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  let className = "";

  // If dragging a single row, change bg of that row
  if (collected.isDragging) {
    className = "dndRow";
  }

  // If dragging a row which is within multi-selected rows, remove the bg styles
  if (multiple) {
    className = "";
  }

  return (
    <>
      <DragPreviewImage connect={dragPreview} src={multiple ? dndMultipleImage : dndSingleImage} />
      <div ref={drag} className={className} style={{ display: "flex" }}>
        {cols}
      </div>
    </>
  );
});

const ContainerRow: FC<any> = observer(({ cols, ndnConfig, rowData }) => {
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: ["item"],
    drop: (item: any) => (canDrop ? ndnConfig.onDrop(item.item, rowData) : null),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: ndnConfig.canDrop ? ndnConfig.canDrop(rowData) : true,
    }),
  });

  let className = "";

  if (isOver && canDrop) {
    className = "dndRow";
  }

  return (
    <div ref={drop} className={className} style={{ display: "flex" }}>
      {cols}
    </div>
  );
});

const Row = observer(({ index, ofTableController }: { index: number; ofTableController: OFTableController }) => {
  const rowData = () => {
    const { sortedFilteredList, getRowPointers } = ofTableController;

    // If the rendered rows were counted before the pointers - ignore
    if (index >= getRowPointers.length) {
      return null;
    }

    const pointer = getRowPointers[index].index;
    const rowData = sortedFilteredList[pointer];

    return rowData;
  };

  const renderDndRow = (cols) => {
    const { props, multiSelectedRows } = ofTableController;

    const ndnConfig = props.ndnConfig;

    switch (ndnConfig.type) {
      case "item": {
        return (
          <DraggableRow multiple={multiSelectedRows.totSelected} cols={cols} ndnConfig={ndnConfig} rowData={rowData} />
        );
      }

      case "container": {
        return <ContainerRow cols={cols} ndnConfig={ndnConfig} rowData={rowData} />;
      }
    }
  };

  const renderRow = () => {
    const { visibleColumns, props } = ofTableController;

    let cols = [];
    visibleColumns.forEach((col, colIndex) => {
      if (col.subColumns) {
        col.subColumns.forEach((subCol, subIndex) => {
          cols.push(
            <RenderCellContent
              key={subCol.id}
              columnIndex={subIndex}
              rowIndex={index}
              ofTableController={ofTableController}
            />
          );
        });
      } else {
        cols.push(
          <RenderCellContent
            key={col.id}
            columnIndex={colIndex}
            rowIndex={index}
            ofTableController={ofTableController}
          />
        );
      }
    });

    // If dnd config is passed to the table, render special rows for this
    if (props.ndnConfig) {
      return renderDndRow(cols);
    }

    return <>{cols}</>;
  };

  return renderRow();
});

export default Row ;
